import { Pause, Play } from 'lucide-react'
import type { AngebotsZustand, FamilyPortal, FamilyRolloutInfo, FamilyVariante, PortalZeile, RolloutPlanAnzeige, ScopeWirkung } from '@/api/priceRollout'
import { NaechsterSchritt } from '@/components/rollout/RolloutAnzeige'
import { fmtEUR } from '@/lib/pricing-format'
import { cn } from '@/lib/utils'
import { WIRKT_NICHT, abstand, angebotsLinks, kontoKurz, paarZustand, portalName, rolloutGrundText, zielFuer, type AngebotsLink, type KopplungsPaar } from './rolloutView'

const fmtTag = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit' }) : '—'

const ZUSTAND: Record<string, { text: string; punkt: string; ton: string }> = {
  aktiv: { text: 'aktiv', punkt: 'bg-success', ton: 'text-ink' },
  unbestaetigt: { text: 'unbestätigt', punkt: 'bg-warn', ton: 'text-ink' },
  offline: { text: 'offline', punkt: 'bg-muted', ton: 'text-ink-2' },
  ausgeschlossen: { text: 'ausgeschlossen', punkt: 'bg-muted-2', ton: 'text-ink-2' },
  kein_angebot: { text: 'kein Angebot', punkt: 'border border-muted bg-transparent', ton: 'text-muted' },
}

export const ROLLOUT_STATUS: Record<string, string> = {
  active: 'aktiv', paused: 'pausiert', error: 'Fehler', done: 'erreicht',
}

/**
 * Preisspur: das erlaubte Band Mindest- … Höchstpreis als Linie, darauf Ist (Ring) und Ziel (Punkt).
 * Nur mit gesetztem Höchstpreis (Ceiling); ohne Ceiling gibt es kein Band, die Untergrenze steht
 * dann als Break-Even/Mindestpreis in der Zelle. Reine Lage-Anzeige der Backend-Werte.
 */
export function Preisspur({ floor, ceiling, ist, ziel }: { floor: number; ceiling: number | null; ist: number | null; ziel: number | null }) {
  if (ceiling === null || !(ceiling > floor)) return null
  const lage = (v: number) => Math.min(1, Math.max(0, (v - floor) / (ceiling - floor))) * 100
  const ausserhalb = (v: number) => v < floor || v > ceiling
  return (
    <div className="pt-1" aria-label={`Erlaubter Preis ${fmtEUR(floor)} bis ${fmtEUR(ceiling)}`}>
      <div className="relative h-2.5">
        <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-line" />
        <div className="absolute left-0 top-0 h-full w-px bg-ink-2/60" />
        <div className="absolute right-0 top-0 h-full w-px bg-ink-2/60" />
        {ist !== null && (
          <span
            title={`Ist ${fmtEUR(ist)}`}
            className={cn(
              'absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] bg-panel',
              ausserhalb(ist) ? 'border-coral' : 'border-ink-2',
            )}
            style={{ left: `${lage(ist)}%` }}
          />
        )}
        {ziel !== null && (
          <span
            title={`Ziel ${fmtEUR(ziel)}`}
            className={cn('absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full', ausserhalb(ziel) ? 'bg-coral' : 'bg-primary')}
            style={{ left: `${lage(ziel)}%` }}
          />
        )}
      </div>
      <div className="flex justify-between font-mono text-[9.5px] leading-none text-muted">
        <span>min. {fmtEUR(floor)}</span>
        <span>max. {fmtEUR(ceiling)}</span>
      </div>
    </div>
  )
}

const STUFE: Record<PortalZeile['kalkulation']['versand_stufe'], string> = {
  brief: 'Brief', warenpost: 'Warenpost', dhl: 'DHL-Paket',
  gls: 'GLS', gls_uebergroesse_150: 'GLS Übergröße ≤ 150 cm', gls_uebergroesse_gt150: 'GLS Übergröße > 150 cm',
}

/** Betrag ohne €-Zeichen für die Herleitung (EK mit bis zu 4 Stellen). */
const zahl = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })

/**
 * Versand je Portal: Kalkulationspolitik (inkl/exkl) und Kundenversand des Angebots. Behauptet nur,
 * was das Angebot meldet; unbekannte Beträge und Widersprüche zur Politik werden markiert.
 */
function versandHinweis(z: PortalZeile, a: AngebotsZustand, portal: string): { text: string; title?: string; warn: boolean } {
  const inkl = z.politik === 'inkl'
  const kalk = inkl ? 'Kalkuliert inkl. Versand (Break-Even enthält den Versand).' : 'Plus-Versand-Politik: Preis = Kalkulation inkl. Versand minus Kundenversand.'
  if (portal === 'shopify' && !inkl && a.versand === null && a.versand_frei !== true && z.kundenversand !== null) {
    return { text: `zzgl. Versand ${fmtEUR(z.kundenversand)} (Einstellung)`, title: kalk, warn: true }
  }
  if (a.versand_frei === null) {
    return { text: inkl ? 'Kalkulation inkl. Versand' : 'Kalkulation zzgl. Versand', title: `${kalk} Versand des Angebots unbekannt.`, warn: !inkl }
  }
  if (a.versand_frei) {
    return inkl
      ? { text: 'inkl. Versand (kostenlos für Kunden)', title: kalk, warn: false }
      : { text: 'Angebot versandkostenfrei – kalkuliert zzgl. Versand', title: kalk, warn: true }
  }
  if (a.versand === null) {
    return { text: inkl ? 'Angebot berechnet Versand (Betrag unbekannt)' : 'zzgl. Versand (Betrag unbekannt)', title: kalk, warn: true }
  }
  return inkl
    ? { text: `zzgl. Versand ${fmtEUR(a.versand)} laut Angebot`, title: `${kalk} Das Angebot berechnet trotzdem Versand.`, warn: true }
    : { text: `zzgl. Versand ${fmtEUR(a.versand)}`, title: kalk, warn: true }
}

/**
 * Untergrenze verständlich: Break-Even mit Herleitung (gleiche Rechnung wie der Floor im Backend),
 * Mindestpreis der Regel, falls er höher liegt, und Verlust je Verkauf beim Ist-Preis.
 */
function Untergrenze({ z }: { z: PortalZeile }) {
  const k = z.kalkulation
  const inkl = z.politik === 'inkl'
  const basis = `EK ${zahl(k.ek)} + Versand ${zahl(k.versand)} (${STUFE[k.versand_stufe]} ${zahl(k.porto)} + Verpackung ${zahl(k.verpackung)}) · Gebühren ${zahl(k.gebuehren_pct).replace(/,00$/, '')} %`
  const herleitung = inkl
    ? basis
    : z.kundenversand === null
      ? `${basis} · Kundenversand unbekannt – gerechnet wie inkl.`
      : `${basis} − Kundenversand ${zahl(z.kundenversand)}`
  return (
    <div className="flex flex-col gap-0.5">
      <div className="font-mono text-[10.5px] font-semibold text-ink-2">
        {inkl || z.kundenversand === null ? `Break-Even ${fmtEUR(k.break_even)}` : `Break-Even zzgl. Versand ${fmtEUR(k.break_even)}`}
      </div>
      <div
        className="text-[10px] leading-snug text-muted"
        title={inkl && k.hochgestuft ? 'Preis über der Brief-Schwelle – Versand als Warenpost gerechnet' : undefined}
      >
        {herleitung}
        {z.set_ek_unbekannt && <span className="font-semibold text-ink-2"> · Set-EK unvollständig</span>}
      </div>
      {k.min_preis !== null && z.floor > k.break_even && (
        <div className="font-mono text-[10px] text-ink-2">Mindestpreis {fmtEUR(z.floor)} (Regel)</div>
      )}
      {z.verlust_je_verkauf !== null && (
        <div role="status" className="mt-0.5 rounded border-l-2 border-l-coral bg-coral-soft px-1.5 py-0.5 text-[10.5px] font-semibold text-ink">
          unter Break-Even – Verlust je Verkauf {fmtEUR(z.verlust_je_verkauf)}
        </div>
      )}
    </div>
  )
}

/** Modus eines Rollouts, der wirkt: LIVE (schreibt echte Preise) oder nur Protokoll. */
function ModusMarke({ wirkung }: { wirkung: ScopeWirkung }) {
  if (wirkung === 'live') {
    return <span className="rounded bg-coral px-1 text-[10px] font-bold tracking-wide text-ink" title="schreibt echte Preise">LIVE</span>
  }
  if (wirkung === 'dry_run') return <span className="text-[10px] text-ink-2" title="kein Preis wird geschrieben">nur Protokoll</span>
  return null
}

/** Nächster Schritt eines laufenden Rollouts (Prognose vom Backend), sonst nichts. */
function PlanZeile({ status, plan }: { status: string; plan?: RolloutPlanAnzeige | null }) {
  if (status !== 'active' || !plan) return null
  return <div className="mt-0.5"><NaechsterSchritt zeitplan={plan.zeitplan} praefix="nächster Schritt" /></div>
}

export function RolloutKnopf({ rollout, portal, wirkung, onPause, onFortsetzen, busy }: {
  rollout: Pick<FamilyRolloutInfo, 'id' | 'status' | 'ziel'> & { fehler?: string | null; plan?: RolloutPlanAnzeige | null }
  portal: string
  /** Was der Tick mit diesem Rollout täte; überspringt er ihn, heißt „aktiv“ nur „angelegt“. */
  wirkung: ScopeWirkung
  onPause: (id: number) => void
  onFortsetzen: () => void
  busy: boolean
}) {
  const name = portalName(portal)
  const still = rollout.status === 'active' ? WIRKT_NICHT[wirkung] : undefined
  const grund = rolloutGrundText(rollout.fehler ?? null)
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5 text-[11px]">
        {still ? (
          <span
            className="rounded bg-warn-soft px-1 ring-1 ring-warn/60 font-medium text-ink"
            title="Der Tageslauf überspringt diesen Rollout – es wird kein Preis geschrieben."
          >
            angelegt, wirkt nicht: {still}
          </span>
        ) : (
          <>
            <span
              className={cn('font-medium', rollout.status === 'error' ? 'text-destructive' : rollout.status === 'active' ? 'text-primary' : 'text-ink-2')}
              title={rollout.fehler ?? undefined}
            >
              Rollout {ROLLOUT_STATUS[rollout.status] ?? rollout.status}
            </span>
            {rollout.status === 'active' && <ModusMarke wirkung={wirkung} />}
          </>
        )}
        <span className="font-mono text-muted">→ {fmtEUR(rollout.ziel)}</span>
        {rollout.status === 'active' && (
          <button
            type="button" disabled={busy} onClick={() => onPause(rollout.id)}
            aria-label={`${name}-Rollout pausieren`} title="Pausieren"
            className="ml-auto inline-flex size-5 items-center justify-center rounded border border-line text-ink-2 hover:bg-panel-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-50"
          >
            <Pause className="size-3" />
          </button>
        )}
        {(rollout.status === 'paused' || rollout.status === 'error') && (
          <button
            type="button" disabled={busy} onClick={onFortsetzen}
            aria-label={`${name}-Rollout fortsetzen`} title="Fortsetzen"
            className="ml-auto inline-flex size-5 items-center justify-center rounded border border-line text-ink-2 hover:bg-panel-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-50"
          >
            <Play className="size-3" />
          </button>
        )}
      </div>
      {!still && <PlanZeile status={rollout.status} plan={rollout.plan} />}
      {grund && <GrundZeile text={grund} roh={rollout.fehler ?? ''} />}
    </div>
  )
}

/** Warum der Tick bei diesem Rollout keinen Schritt macht (error_message in Klartext, roh als Tooltip). */
function GrundZeile({ text, roh }: { text: string; roh: string }) {
  return (
    <div role="status" title={roh} className="rounded-sm border-l-2 border-l-warn bg-warn-soft px-1.5 py-px text-[10px] leading-snug text-ink">
      {text}
    </div>
  )
}

const KNOPF = 'inline-flex size-5 items-center justify-center rounded border border-line text-ink-2 hover:bg-panel-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-50'

/**
 * Basis + eBay als EIN Rollout (Plan 2026-10-19 S2.3): beide laufen im selben Takt mit demselben
 * Schritt. Die Klammer aus zwei Portalstrichen zeigt die Kopplung; Pause/Fortsetzen wirkt auf beide.
 */
export function PaarKnopf({ paar, wirkung, onPause, onFortsetzen, busy }: {
  paar: KopplungsPaar
  /** Wirkungen beider Scopes (Basis, eBay): überspringt der Tick einen, heißt „aktiv“ nur „angelegt“. */
  wirkung: { basis: ScopeWirkung; ebay: ScopeWirkung }
  onPause: (id: number) => void
  onFortsetzen: (id: number) => void
  busy: boolean
}) {
  const z = paarZustand(paar)
  const stillBasis = paar.basis.status === 'active' ? WIRKT_NICHT[wirkung.basis] : undefined
  const stillEbay = paar.ebay.status === 'active' ? WIRKT_NICHT[wirkung.ebay] : undefined
  const still = stillBasis ? `Basis: ${stillBasis}` : stillEbay ? `eBay: ${stillEbay}` : undefined
  const statusText = z.status === 'gemischt'
    ? `Basis ${ROLLOUT_STATUS[paar.basis.status] ?? paar.basis.status} · eBay ${ROLLOUT_STATUS[paar.ebay.status] ?? paar.ebay.status}`
    : `Rollout ${ROLLOUT_STATUS[z.status] ?? z.status}`
  const gleichesZiel = Math.abs(paar.basis.ziel - paar.ebay.ziel) < 0.005
  return (
    <div className="flex flex-col gap-0.5" role="group" aria-label="Basis und eBay gekoppelt">
      <div className="flex items-center gap-1.5 text-[11px]">
        <span aria-hidden className="inline-flex h-3 items-stretch gap-[2px]" title="gekoppelt">
          <span className="w-[3px] rounded-sm bg-ink-2" />
          <span className="w-[3px] rounded-sm bg-[#0288D1]" />
        </span>
        {still ? (
          <span className="rounded bg-warn-soft px-1 ring-1 ring-warn/60 font-medium text-ink" title="Der Tageslauf überspringt diesen Rollout – es wird kein Preis geschrieben.">
            angelegt, wirkt nicht: {still}
          </span>
        ) : (
          <>
            <span className={cn('font-medium', z.status === 'error' ? 'text-destructive' : z.status === 'active' ? 'text-primary' : 'text-ink-2')}>
              {statusText}
            </span>
            {z.status === 'active' && <ModusMarke wirkung={wirkung.basis} />}
          </>
        )}
        <span className="font-mono text-muted">
          → {fmtEUR(paar.basis.ziel)}{gleichesZiel ? '' : ` · eBay ${fmtEUR(paar.ebay.ziel)}`}
        </span>
        {z.pauseId !== null && (
          <button type="button" disabled={busy} onClick={() => onPause(z.pauseId!)} aria-label="Basis- und eBay-Rollout pausieren" title="Beide pausieren" className={cn('ml-auto', KNOPF)}>
            <Pause className="size-3" />
          </button>
        )}
        {z.fortsetzenId !== null && (
          <button type="button" disabled={busy} onClick={() => onFortsetzen(z.fortsetzenId!)} aria-label="Basis- und eBay-Rollout fortsetzen" title="Beide fortsetzen" className={cn(z.pauseId === null && 'ml-auto', KNOPF)}>
            <Play className="size-3" />
          </button>
        )}
      </div>
      <div className="text-[10px] text-muted">Basis + eBay gekoppelt – zwei Rollouts, ein Takt, gleicher Schritt</div>
      {!still && <PlanZeile status={z.status} plan={paar.basis.plan ?? paar.ebay.plan} />}
      {z.grund && <GrundZeile text={z.grund} roh={[paar.basis.fehler, paar.ebay.fehler].filter(Boolean).join(' · ')} />}
    </div>
  )
}

interface Props {
  v: FamilyVariante
  portal: FamilyPortal
  /** Rollout dieses Portals in der Zelle zeigen (Amazon steht in der Start-Spalte). */
  mitRollout: boolean
  /** eBay läuft gekoppelt mit der Basis: der Rollout steht als eine Zeile in der Start-Spalte. */
  gekoppelt?: boolean
  onPause: (id: number) => void
  onFortsetzen: (portal: string, rollout: FamilyRolloutInfo) => void
  busy: boolean
}

export function PortalZelle({ v, portal, mitRollout, gekoppelt = false, onPause, onFortsetzen, busy }: Props) {
  const z = v.portale[portal]
  const a = z.angebot
  const zs = ZUSTAND[a.zustand] ?? ZUSTAND.kein_angebot!
  const ziel = zielFuer(v, portal)
  const aufschlag = portal === 'ebay' ? v.start.base.scopes.find(s => s.portal === portal)?.aufschlag : null
  const delta = abstand(v, portal)
  const versand = versandHinweis(z, a, portal)
  return (
    <div className="flex min-w-[150px] flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px]">
        <span className={cn('inline-block size-1.5 shrink-0 rounded-full', zs.punkt)} aria-hidden />
        <span className={cn('font-medium', zs.ton)}>
          {zs.text}{a.zustand === 'offline' && a.offline_seit ? ` seit ${fmtTag(a.offline_seit)}` : ''}
        </span>
        {a.konten > 1 && <span className="text-muted">· {a.konten} Konten</span>}
        {z.cia_aktiv && a.zustand !== 'aktiv' && (
          <span className="rounded bg-warn-soft px-1 ring-1 ring-warn/60 text-[10px] text-ink" title="DreamRobot (cia) meldet aktiv, das echte Angebot ist es nicht">DR: aktiv</span>
        )}
        {!z.cia_aktiv && a.zustand === 'aktiv' && (
          <span className="rounded bg-panel-2 px-1 text-[10px] text-ink-2" title="Angebot online, in DreamRobot (cia) aber aus">DR: aus</span>
        )}
      </div>
      {delta === 0 ? (
        <div className="flex items-baseline gap-1.5 font-mono text-xs tabular-nums">
          <span className="font-semibold text-ink" title="Ist-Preis des Angebots = Zielpreis">{fmtEUR(a.ist_preis)}</span>
          <span className="rounded bg-success-soft px-1 font-sans text-[10px] font-medium text-success" title="Das Angebot steht schon auf dem Zielpreis – ein Rollout ändert nichts">am Ziel</span>
        </div>
      ) : (
        <div className="flex items-baseline gap-1.5 font-mono text-xs tabular-nums">
          <span className={a.ist_preis === null ? 'text-muted' : 'text-ink-2'} title="Ist-Preis des Angebots">{fmtEUR(a.ist_preis)}</span>
          <span className="text-muted" aria-hidden>→</span>
          <span className="font-semibold text-ink" title="Zielpreis (Backend)">{fmtEUR(ziel)}</span>
          {delta !== null && (
            <span className="text-[10.5px] text-ink-2" title="Abstand Ist → Ziel">
              {delta > 0 ? '▲' : '▼'} {fmtEUR(Math.abs(delta))}
            </span>
          )}
        </div>
      )}
      {aufschlag && (aufschlag.wirksam_ct > 0 || aufschlag.hinweis === 'mehrere_konten' || aufschlag.hinweis === 'gekappt') && <div className="text-[10px] text-muted">
        {aufschlag.wirksam_ct > 0 && <div>inkl. +{aufschlag.wirksam_ct} ct {aufschlag.konto ? kontoKurz(aufschlag.konto) : ''}</div>}
        {aufschlag.hinweis === 'mehrere_konten' && <div>kein Aufschlag – mehrere eBay-Konten</div>}
        {aufschlag.hinweis === 'gekappt' && <div>Aufschlag gekappt am Höchstpreis</div>}
      </div>}
      <div>
        <span
          className={cn('text-[10px]', versand.warn ? 'rounded bg-warn-soft px-1 ring-1 ring-warn/60 font-semibold text-ink' : 'text-ink-2')}
          title={versand.title}
        >
          {versand.text}
        </span>
      </div>
      <Untergrenze z={z} />
      <Preisspur floor={z.floor} ceiling={z.ceiling} ist={a.ist_preis} ziel={ziel} />
      {gekoppelt && z.rollout && (
        <div className="text-[10.5px] text-ink-2">Rollout gekoppelt mit Basis – siehe Start</div>
      )}
      {mitRollout && !gekoppelt && z.rollout && (
        <RolloutKnopf rollout={z.rollout} portal={portal} wirkung={z.wirkung} onPause={onPause} onFortsetzen={() => onFortsetzen(portal, z.rollout!)} busy={busy} />
      )}
    </div>
  )
}

/**
 * Echte Angebote der Variante zum Nachprüfen: je Portal und URL ein kleiner Link in neuem Tab,
 * unabhängig vom Portalfilter. Offline/ausgeschlossen gedämpft, Status und Konto im Tooltip.
 */
export function AngebotsLinks({ v, links = angebotsLinks(v) }: { v: FamilyVariante; links?: AngebotsLink[] }) {
  if (links.length === 0) return null
  return (
    <div className="mt-0.5 flex flex-col items-start gap-0.5 text-[11px]">
      {links.map((l) => <AngebotsLinkZeile key={`${l.portal}-${l.url}`} l={l} />)}
    </div>
  )
}

/** Ein Angebot: Portal, Konto-Marke(n), Nummer (eBay-ItemID / ASIN) — gleiche Nummer = gleiches Angebot. */
export function AngebotsLinkZeile({ l }: { l: AngebotsLink }) {
  return (
    <a
      href={l.url} target="_blank" rel="noopener noreferrer" title={l.titel}
      aria-label={`${portalName(l.portal)}-Angebot${l.nummer ? ` ${l.nummer}` : l.nr === null ? '' : ` ${l.nr}`}${l.parent && l.parent !== l.nummer ? `, Parent ${l.parent}` : ''}, Konto ${l.konten.map(kontoKurz).join(', ')}, öffnen (neuer Tab)`}
      data-gedaempft={l.aktiv ? undefined : 'true'}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap hover:underline focus-visible:outline-2 focus-visible:outline-primary',
        l.aktiv ? 'text-primary' : 'text-muted hover:text-ink-2',
      )}
    >
      <span>{l.nummer ? portalName(l.portal) : l.text}</span>
      {l.konten.map((k) => (
        <span key={k} className="rounded bg-panel-2 px-1 py-px text-[10px] font-semibold text-ink-2 ring-1 ring-line">{kontoKurz(k)}</span>
      ))}
      {l.nummer && <span className="font-mono">{l.nummer}</span>}
      {l.parent && l.parent !== l.nummer && (
        <span className="text-[10px] text-ink-2" title="Parent-ASIN der Amazon-Variationsfamilie">
          Parent <span className="font-mono">{l.parent}</span>
        </span>
      )}
      <span aria-hidden>↗</span>
    </a>
  )
}
